<template>
  <page-view
    :backLink="backLink"
    :backText="backText"
    class="subpage customer-activity"
    :title="customer ? customer.email : state === 'loading' ? 'Loading customer' : 'Could not find customer'"
  >
    <template v-slot:id>
      <template v-if="customer">
        <id :id="customer.id" />
      </template>
    </template>
    <template v-slot:header-right>
      <template v-if="customer">
        <dots class="border left m-r-2">
          <ul>
            <li @click="deleteCustomer">
              <i class="icon-close-medium" />
                Delete customer
            </li>
            <li @click="exportCustomer">
              <i class="icon-expand rotate-90" />
              Export customer
            </li>
          </ul>
        </dots>
      </template>
    </template>
    <template v-if="customer">
      <div class="d-f fd-c fd-md-r">
        <div class="f-1">
          <card-minimal title="Summary" class="summary">
            <div class="p-a-4">
              <p class="card-content-title">Email</p>
              <p>{{ customer.email || 'User has opted out' }}</p>
              <template v-if="customer.name">
                <hr class="m-y-2" />
                <p class="card-content-title">Name</p>
                <p>{{ customer.name }}</p>
              </template>
              <template v-if="customer.phone">
                <hr class="m-y-2" />
                <p class="card-content-title">Phone</p>
                <p>{{ customer.phone }}</p>
              </template>
              <hr class="m-y-2" />
              <p class="card-content-title">Last activity</p>
              <p>{{ customer.last_activity | formatdate('MMMM DD, YYYY, HH:mm') }}</p>
              <hr class="m-y-2" />
              <p class="card-content-title">Created</p>
              <p>{{ customer.created_at | formatdate('MMMM DD, YYYY, HH:mm') }}</p>
            </div>
          </card-minimal>
          <Button icon="edit-pen" class="f-w m-t-4" @click="addNote = true" :disabled="noteState === 'creating'" :loading="noteState === 'creating'">Create customer note</Button>
        </div>
        <div class="f-2 m-l-sm-0 m-l-4 m-t-sm-3">
          <card-minimal-title title="Activity" />
          <template v-if="activities.length === 0">
            <div class="ta-c p-a-4">
              <p class="grey">Customer have no activity</p>
            </div>
          </template>
          <template v-else>
            <card-minimal class="m-b-4" v-for="(date, index) in activities" :key="index" :subtitle="date.date | formatdate('MMMM YYYY')">
              <div class="p-x-4 p-b-2">
                <Activity v-for="(event, evIndex) in date.events" :activity="event" :customer="customer.id" :key="evIndex" @fetchActivities="getActivities" />
              </div>
            </card-minimal>
          </template>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="d-f f-1 ai-c jc-c" v-if="state === 'loading'">
        <div class="loading-spinner large"></div>
      </div>
      <div class="d-f f-1 ai-c jc-c" v-else-if="state === 'error'">
        <p>Could not load customer</p>
      </div>
    </template>
    <portal to="modals">
      <modal :show="addNote" @close="addNote = false" class="auto-height customer-activity-modal" title="Add note">
        <div class="p-a-4">
          <form class="d-f" @submit.prevent="createNote">
            <form-textarea class="small f-w m-b-2" :toolbar="['bold', 'ul', 'link']" v-model="note" />
          </form>
        </div>
        <template v-slot:footer>
          <div class="d-f f-w">
            <Button class="no-wrap" @click="createNote">Add note</Button>
            <Button @click="note = ''; addNote = false;" class="red outline ml-a">Cancel</Button>
          </div>
        </template>
      </modal>
    </portal>
  </page-view>
</template>

<script>

import { exportToCsv } from '@/utils';

import Activity from '@/components/Customers/Activity.vue';

import Dots from '@/components/Dots.vue';

export default {
  components: {
    Activity,
    Dots,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      activities: [],
      customer: null,
      state: 'loading',
      backLink: '/customers',
      addNote: false,
      note: '',
      noteState: '',
    };
  },
  methods: {
    async createNote() {
      this.noteState = 'creating';

      const success = await this.$store.dispatch('Customers/addNote', {
        id: this.customer.id,
        note: this.note,
      });

      this.noteState = '';

      if (!success) {
        swal({ title: 'An error occured', text: 'Could not add note', icon: 'error' });
        return;
      }

      this.note = '';
      this.addNote = false;

      this.getActivities();
    },
    async exportCustomer() {
      const data = await this.$store.dispatch('Customers/exportCustomers', [this.customer.id]);
      if (!data) {
        swal({ title: 'An error occured', text: 'Could not export customer', icon: 'error' });
        return;
      }

      exportToCsv('customers_export.csv', data);
    },
    async deleteCustomer() {
      const willDelete = await swal({
        title: this.$t('errors.are_you_sure'), text: 'This will remove customer', icon: 'warning', buttons: true, dangerMode: true,
      });

      if (!willDelete) {
        return;
      }

      const deleted = await this.$store.dispatch('Customers/deleteCustomers', [this.customer.id]);
      if (!deleted) {
        swal({ title: 'An error occured', text: 'Could not delete customer', icon: 'error' });
      }
      this.$router.push(this.backLink);
    },
    /**
     * Get customer information
     */
    async getCustomer() {
      this.customer = await this.$store.dispatch('Customers/getCustomer', this.id);
    },
    /**
     * Get customer activities
     */
    async getActivities() {
      const activities = await this.$store.dispatch('Customers/getCustomerActivity', this.id) || [];
      this.activities = activities.map(month => ({
        events: month.events.reduce((evs, ev) => {
          const index = evs.findIndex(fi => ev.metadata.giveaway_id === fi.metadata.giveaway_id);
          if (index < 0 || typeof ev.metadata.giveaway_id === 'undefined') {
            evs.push(ev);
          } else {
            // eslint-disable-next-line
            evs[index].metadata = { ...ev.metadata, ...evs[index].metadata };
          }
          return evs;
        }, []),
        date: month.date,
      }));
    },
  },
  async mounted() {
    await this.getCustomer();
    await this.getActivities();
    this.state = '';

    const lastRoute = this.$store.getters['Theme/lastRoute'];
    if (lastRoute && lastRoute.path.includes('customers')) {
      this.backLink = lastRoute.fullPath;
      this.backText = 'Back to Customers';
    }

    if (lastRoute && lastRoute.path.includes('form')) {
      this.backLink = lastRoute.fullPath;
      this.backText = 'Back to Form';
    }

    if (lastRoute && lastRoute.path.includes('giveaway')) {
      this.backLink = lastRoute.fullPath;
      this.backText = 'Back to Giveaway';
    }
  },
};
</script>

<style lang="scss">
.customer-activity-modal {
  .textarea {
    & > div {
      height: auto !important;
    }
  }
}
</style>
